import type {
  ChildReportCard,
  ReportSubjectRow,
  ReportTermKey,
} from "../types/parentFamily";

/** CAPS (SA) 7-point achievement scale */
export const CAPS_LEVELS = [
  { level: 7, min: 80, max: 100, description: "Outstanding Achievement" },
  { level: 6, min: 70, max: 79, description: "Meritorious Achievement" },
  { level: 5, min: 60, max: 69, description: "Substantial Achievement" },
  { level: 4, min: 50, max: 59, description: "Adequate Achievement" },
  { level: 3, min: 40, max: 49, description: "Moderate Achievement" },
  { level: 2, min: 30, max: 39, description: "Elementary Achievement" },
  { level: 1, min: 0, max: 29, description: "Not Achieved" },
] as const;

export const REPORT_TERM_KEYS: ReportTermKey[] = [
  "term1",
  "term2",
  "term3",
  "promotion",
];

export const REPORT_TERM_LABELS: Record<ReportTermKey, string> = {
  term1: "Term 1",
  term2: "Term 2",
  term3: "Term 3",
  promotion: "Promotion",
};

/** Default Intermediate Phase subject list (editable in the form). */
export const DEFAULT_INTERMEDIATE_SUBJECTS: Array<Pick<ReportSubjectRow, "name">> =
  [
    { name: "English HL" },
    { name: "Afrikaans FAL" },
    { name: "Mathematics" },
    { name: "Natural Sciences" },
    { name: "Life Skills" },
    { name: "Social Sciences" },
  ];

export const REPORT_TITLE_OPTIONS = [
  "Progress Report",
  "Report Card",
  "Mid-year Report",
  "Final Report",
] as const;

export const REPORT_PHASE_OPTIONS = [
  "Foundation Phase",
  "Intermediate Phase",
  "Senior Phase",
  "FET Phase",
] as const;

export function academicYearOptions(aroundYear = new Date().getFullYear()): string[] {
  return [String(aroundYear)];
}

export function buildPeriodLabel(
  term: ReportTermKey,
  academicYear: string
): string {
  const year = academicYear.trim() || String(new Date().getFullYear());
  return `${REPORT_TERM_LABELS[term]} ${year}`;
}

/** Infer which term this card is primarily for (form UI is single-term). */
export function detectPrimaryTerm(report: ChildReportCard): ReportTermKey {
  const label = (report.periodLabel || "").toLowerCase();
  if (label.includes("promotion")) return "promotion";
  if (label.includes("term 3") || label.includes("term3")) return "term3";
  if (label.includes("term 2") || label.includes("term2")) return "term2";
  if (label.includes("term 1") || label.includes("term1")) return "term1";

  let best: ReportTermKey = "term1";
  let bestCount = -1;
  for (const term of REPORT_TERM_KEYS) {
    const count = (report.subjects || []).filter(
      (s) => typeof s.terms?.[term]?.percent === "number"
    ).length;
    if (count > bestCount) {
      bestCount = count;
      best = term;
    }
  }
  return best;
}

export function capsLevelForPercent(
  percent: number | null | undefined
): (typeof CAPS_LEVELS)[number] | null {
  if (percent == null || Number.isNaN(percent)) return null;
  const p = Math.max(0, Math.min(100, percent));
  return CAPS_LEVELS.find((l) => p >= l.min && p <= l.max) || null;
}

export function createSubjectRow(name: string): ReportSubjectRow {
  return {
    id: `subj-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name,
    terms: {},
  };
}

export function defaultSubjects(): ReportSubjectRow[] {
  return DEFAULT_INTERMEDIATE_SUBJECTS.map((s) => createSubjectRow(s.name));
}

/** Average of subject % marks for a term (skips empty). */
export function computeLearnerAverage(
  subjects: ReportSubjectRow[],
  term: ReportTermKey
): number | null {
  const values = subjects
    .map((s) => s.terms?.[term]?.percent)
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  if (values.length === 0) return null;
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

export function formatPercent(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `${Math.round(value)}`;
}

export function reportCardSummaryLine(report: ChildReportCard): string {
  const subjects = Array.isArray(report.subjects) ? report.subjects : [];
  const avg =
    report.learnerAverages?.term3 ??
    report.learnerAverages?.term2 ??
    report.learnerAverages?.term1 ??
    computeLearnerAverage(subjects, "term3") ??
    computeLearnerAverage(subjects, "term2") ??
    computeLearnerAverage(subjects, "term1") ??
    null;
  const level = capsLevelForPercent(avg);
  const parts = [
    report.title,
    report.overallResult,
    avg != null ? `Avg ${avg}%` : null,
    level ? `L${level.level}` : null,
    report.issuedDate ? `Issued ${report.issuedDate.slice(0, 10)}` : null,
  ].filter(Boolean);
  return parts.join(" · ") || "No details yet";
}

/** Migrate legacy flat cards so UI always has subjects array. */
export function normalizeReportCard(report: ChildReportCard): ChildReportCard {
  return {
    ...report,
    subjects: Array.isArray(report.subjects) ? report.subjects : [],
    learnerAverages: report.learnerAverages || {},
    teacherComment: report.teacherComment ?? report.notes,
  };
}
